/* global gdAlertify */
import React, { useState } from "react";
import axiosInstance from "../config/axiosConfig";
import { formatDate, formatDateTime, formatMoney } from "../utils/utils";
import { ACTION_PERMISSIONS, hasActionPermission } from "../utils/authPermissions";
import BuscarPaciente from "./components/BuscarPaciente";

const CAMPOS_FAMILIA = [
    { key: "valorTurnoPaciente", label: "Valor turno" },
    { key: "valorTurnoBasePaciente", label: "Valor base" },
    { key: "valorColacionPaciente", label: "Colación" },
    { key: "valorLocomocionPaciente", label: "Movilización" },
    { key: "valorOtroPaciente", label: "Otro" },
];

const CAMPOS_ACUERDO = [
    { key: "valorTurno", label: "Turno" },
    { key: "valorColacion", label: "Colación" },
    { key: "valorMovilizacion", label: "Movilización" },
];

const PERIODO_CUBIERTO = { 1: "Día", 2: "Noche", 3: "Día y noche" };
const TIPO_COLACION = { 1: "Día", 2: "Noche", 3: "Ambos" };

const hoy = () => new Date().toISOString().slice(0, 10);

const ReajusteValoresPaciente = () => {
    const puedeEditarValorTurno = hasActionPermission(ACTION_PERMISSIONS.EDITAR_ACUERDO_VALOR_TURNO);
    const [pacienteID, setPacienteID] = useState(null);
    const [datos, setDatos] = useState(null);
    const [fecha, setFecha] = useState(hoy());
    const [observacion, setObservacion] = useState("");
    const [familia, setFamilia] = useState({});
    const [acuerdos, setAcuerdos] = useState({});
    const [preview, setPreview] = useState(null);
    const [expandidos, setExpandidos] = useState({});
    const [loading, setLoading] = useState(false);
    const [procesando, setProcesando] = useState(false);

    const cargarDatos = async (id) => {
        setLoading(true);
        try {
            const resp = await axiosInstance.get(`/financiero/reajuste-paciente/${id}`);
            if (resp.data.error) throw new Error(resp.data.message);
            setDatos(resp.data.data);
        } catch (e) {
            gdAlertify.notiError(e.message || "No se pudieron cargar los datos del paciente.");
            setDatos(null);
        } finally {
            setLoading(false);
        }
    };

    const limpiarCambios = () => {
        setFamilia({});
        setAcuerdos({});
        setPreview(null);
        setExpandidos({});
    };

    const seleccionarPaciente = (paciente) => {
        limpiarCambios();
        if (!paciente) {
            setPacienteID(null);
            setDatos(null);
            return;
        }
        setPacienteID(paciente.id);
        cargarDatos(paciente.id);
    };

    // Cualquier cambio invalida la vista previa: hay que volver a previsualizar antes de aplicar.
    const cambiarFamilia = (campo, valor) => {
        setPreview(null);
        setFamilia(prev => ({ ...prev, [campo]: valor }));
    };

    const cambiarAcuerdo = (idAcuerdo, campo, valor) => {
        setPreview(null);
        setAcuerdos(prev => ({ ...prev, [idAcuerdo]: { ...(prev[idAcuerdo] || {}), [campo]: valor } }));
    };

    const construirPayload = () => ({
        pacienteID,
        fecha,
        observacion,
        familia: Object.fromEntries(Object.entries(familia).filter(([, v]) => v !== "" && v !== null)),
        acuerdos: Object.entries(acuerdos)
            .map(([idAcuerdo, valores]) => ({
                idAcuerdo: Number(idAcuerdo),
                ...Object.fromEntries(Object.entries(valores).filter(([, v]) => v !== "" && v !== null)),
            }))
            .filter(a => Object.keys(a).length > 1),
    });

    const previsualizar = async () => {
        setProcesando(true);
        try {
            const resp = await axiosInstance.post("/financiero/reajuste-paciente/previsualizar", construirPayload());
            if (resp.data.error) throw new Error(resp.data.message);
            const data = resp.data.data;
            if (data.familia.length === 0 && data.acuerdos.length === 0) {
                gdAlertify.notiError("No hay cambios respecto a los valores actuales.");
                setPreview(null);
                return;
            }
            setPreview(data);
        } catch (e) {
            gdAlertify.notiError(e.message || "No se pudo generar la vista previa.");
        } finally {
            setProcesando(false);
        }
    };

    const aplicar = () => {
        if (!observacion.trim()) {
            gdAlertify.notiError("Debe ingresar el motivo del reajuste.");
            return;
        }
        const liquidados = preview.periodos_liquidados.length > 0
            ? ` Se reliquidará a este paciente en: ${preview.periodos_liquidados.join(", ")}.`
            : "";
        gdAlertify.confirm({
            title: "Aplicar reajuste",
            message: `¿Aplicar los cambios de ${preview.paciente} desde el ${formatDate(fecha)}?${liquidados}`,
            onConfirm: async () => {
                setProcesando(true);
                try {
                    const resp = await axiosInstance.post("/financiero/reajuste-paciente/aplicar", construirPayload());
                    if (resp.data.error) throw new Error(resp.data.message);
                    const { reliquidados = [], errores_reliquidacion = [] } = resp.data.data;
                    if (errores_reliquidacion.length > 0) {
                        gdAlertify.alertError(
                            `Reajuste aplicado, pero no se pudo reliquidar: ${errores_reliquidacion.map(e => `${e.periodo} (${e.error})`).join(", ")}. Reliquide manualmente desde Cobranza.`
                        );
                    } else {
                        gdAlertify.notiSuccess(
                            `Reajuste aplicado.${reliquidados.length ? ` Reliquidado: ${reliquidados.join(", ")}.` : ""}`
                        );
                    }
                    limpiarCambios();
                    setObservacion("");
                    cargarDatos(pacienteID);
                } catch (e) {
                    gdAlertify.notiError(e.message || "No se pudo aplicar el reajuste.");
                } finally {
                    setProcesando(false);
                }
            },
            onCancel: () => {},
        });
    };

    const hayCambios = Object.values(familia).some(v => v !== "" && v !== null)
        || Object.values(acuerdos).some(a => Object.values(a).some(v => v !== "" && v !== null));

    const inputValor = (valor, onChange, actual, disabled = false) => (
        <input
            type="number"
            min="0"
            disabled={disabled}
            title={disabled ? "Sin permiso para editar el valor turno de acuerdos" : undefined}
            className={`form-control form-control-sm text-end ${valor !== undefined && valor !== "" && Number(valor) !== Number(actual) ? "border-warning fw-bold" : ""}`}
            value={valor ?? ""}
            placeholder={formatMoney(actual)}
            onChange={(e) => onChange(e.target.value)}
            style={{ minWidth: 110 }}
        />
    );

    const f = datos?.familia;

    return (
        <div className="container-fluid py-3">
            <div className="card">
                <div className="card-body">
                    <h3 className="mb-1">Reajuste de Valores por Paciente</h3>
                    <div className="text-muted small mb-3">
                        Cambia desde una fecha el cobro a la familia y el pago a cada auxiliar (acuerdos). Solo se
                        actualizan valores: la rotación y la asignación de turnos no cambian.
                    </div>
                    <div className="row g-3 align-items-end">
                        <div className="col-md-5">
                            <BuscarPaciente onSelect={seleccionarPaciente} onClear={() => seleccionarPaciente(null)} />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-bold text-muted mb-1">Desde fecha</label>
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={fecha}
                                onChange={(e) => { setFecha(e.target.value); setPreview(null); }}
                            />
                        </div>
                        <div className="col-md-5">
                            <label className="form-label small fw-bold text-muted mb-1">Motivo</label>
                            <input
                                className="form-control form-control-sm"
                                value={observacion}
                                onChange={(e) => setObservacion(e.target.value)}
                                placeholder="Ej: cambio de auxiliar, nuevo valor acordado con la familia"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {loading && <div className="text-muted mt-3">Cargando datos del paciente...</div>}

            {datos && !loading && (
                <>
                    <div className="row g-3 mt-0">
                        <div className="col-lg-5">
                            <div className="card h-100">
                                <div className="card-body">
                                    <h5 className="mb-1">Cobro a la familia</h5>
                                    <div className="text-muted small mb-2">
                                        Aplica a todos los turnos del paciente desde la fecha, sin importar el auxiliar.
                                    </div>
                                    {!f ? (
                                        <div className="alert alert-warning py-2 small mb-0">
                                            El paciente no tiene valores de servicio configurados.
                                        </div>
                                    ) : (
                                        <>
                                            <table className="table table-sm align-middle mb-2">
                                                <thead className="table-light">
                                                    <tr><th>Concepto</th><th className="text-end">Actual</th><th className="text-end">Nuevo</th></tr>
                                                </thead>
                                                <tbody>
                                                    {CAMPOS_FAMILIA.map(c => (
                                                        <tr key={c.key}>
                                                            <td>{c.label}</td>
                                                            <td className="text-end">${formatMoney(f[c.key])}</td>
                                                            <td>{inputValor(familia[c.key], (v) => cambiarFamilia(c.key, v), f[c.key])}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            <div className="text-muted small">
                                                Próximo reajuste semestral: <b>{f.proximo_reajuste_familia ? formatDate(f.proximo_reajuste_familia) : "-"}</b> (no se modifica).
                                            </div>
                                            {f.tipoValorAlternativoPaciente === 2 && f.valorTurnoAlternativoPaciente > 0 && (
                                                <div className="alert alert-warning py-2 small mt-2 mb-0">
                                                    Tiene valor fin de semana (familia) de ${formatMoney(f.valorTurnoAlternativoPaciente)}: sábados
                                                    y domingos se cobra el mayor entre ese valor y el valor turno. Ese valor no cambia aquí.
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-7">
                            <div className="card h-100">
                                <div className="card-body">
                                    <h5 className="mb-1">Pago a auxiliares (acuerdos activos)</h5>
                                    <div className="text-muted small mb-2">
                                        Recalcula los turnos de cada auxiliar desde la fecha (horas del día y recargo feriado incluidos).
                                        Turnos en $0 y reemplazos no se tocan.
                                    </div>
                                    {datos.acuerdos.length === 0 ? (
                                        <div className="text-muted small">El paciente no tiene acuerdos activos.</div>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-sm align-middle mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Auxiliar</th>
                                                        {CAMPOS_ACUERDO.map(c => <th key={c.key} className="text-end">{c.label}</th>)}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {datos.acuerdos.map(a => (
                                                        <tr key={a.idAcuerdo}>
                                                            <td>
                                                                <div className="fw-semibold">{a.auxiliarNombre}</div>
                                                                <div className="text-muted small">
                                                                    #{a.idAcuerdo} · desde {formatDate(a.fechaInicio)} · {PERIODO_CUBIERTO[a.periodo_cubierto] || "-"} · colación {TIPO_COLACION[a.tipo_colacion] || "-"}
                                                                </div>
                                                            </td>
                                                            {CAMPOS_ACUERDO.map(c => (
                                                                <td key={c.key}>
                                                                    <div className="text-muted small text-end">${formatMoney(a[c.key])}</div>
                                                                    {inputValor(
                                                                        acuerdos[a.idAcuerdo]?.[c.key],
                                                                        (v) => cambiarAcuerdo(a.idAcuerdo, c.key, v),
                                                                        a[c.key],
                                                                        c.key === "valorTurno" && !puedeEditarValorTurno
                                                                    )}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <button className="btn btn-outline-secondary btn-sm mb-0" onClick={limpiarCambios} disabled={procesando || !hayCambios}>
                            Limpiar
                        </button>
                        <button className="btn btn-primary btn-sm mb-0" onClick={previsualizar} disabled={procesando || !hayCambios || !fecha}>
                            {procesando && !preview ? "Calculando..." : "Vista previa"}
                        </button>
                    </div>

                    {preview && (
                        <div className="card mt-3 border-primary">
                            <div className="card-body">
                                <h5 className="mb-3">Vista previa desde el {formatDate(preview.fecha)}</h5>

                                {preview.periodos_liquidados.length > 0 && (
                                    <div className="alert alert-info py-2 small">
                                        Periodos ya liquidados afectados: <b>{preview.periodos_liquidados.join(", ")}</b>.
                                        Al aplicar se reliquidará solo a este paciente en esos periodos.
                                    </div>
                                )}

                                {preview.familia.length > 0 && (
                                    <div className="mb-3">
                                        <div className="fw-semibold mb-1">Cobro a la familia</div>
                                        <ul className="mb-0 small">
                                            {preview.familia.map(c => (
                                                <li key={c.campo}>{c.label}: ${formatMoney(c.anterior)} → <b>${formatMoney(c.nuevo)}</b></li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {preview.acuerdos.map(a => (
                                    <div key={a.idAcuerdo} className="border rounded p-2 mb-2">
                                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                            <div>
                                                <div className="fw-semibold">{a.auxiliarNombre} <span className="text-muted small">#{a.idAcuerdo}</span></div>
                                                <div className="small">
                                                    {CAMPOS_ACUERDO.filter(c => a.antes[c.key] !== a.despues[c.key]).map(c => (
                                                        <span key={c.key} className="me-3">{c.label}: ${formatMoney(a.antes[c.key])} → <b>${formatMoney(a.despues[c.key])}</b></span>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="text-end small">
                                                <div>{a.cantidad_turnos} turno(s) a recalcular</div>
                                                <div>Turno + colación: ${formatMoney(a.total_antes)} → <b>${formatMoney(a.total_despues)}</b></div>
                                                {a.antes.valorMovilizacion !== a.despues.valorMovilizacion && (
                                                    <div className="text-muted">Movilización se recalcula por tramos al aplicar.</div>
                                                )}
                                            </div>
                                        </div>
                                        {a.turnos.length > 0 && (
                                            <>
                                                <button
                                                    className="btn btn-link btn-sm p-0 mt-1 mb-0"
                                                    onClick={() => setExpandidos(prev => ({ ...prev, [a.idAcuerdo]: !prev[a.idAcuerdo] }))}
                                                >
                                                    {expandidos[a.idAcuerdo] ? "Ocultar turnos" : "Ver turnos"}
                                                </button>
                                                {expandidos[a.idAcuerdo] && (
                                                    <div className="table-responsive mt-2" style={{ maxHeight: 300 }}>
                                                        <table className="table table-sm table-bordered mb-0 small">
                                                            <thead className="table-light sticky-top">
                                                                <tr><th>Fecha</th><th>Turno</th><th className="text-end">Valor</th><th className="text-end">Colación</th></tr>
                                                            </thead>
                                                            <tbody>
                                                                {a.turnos.map(t => (
                                                                    <tr key={`${t.fecha}-${t.periodo}`}>
                                                                        <td>{formatDate(t.fecha)}</td>
                                                                        <td>{t.periodo === "day" ? "Día" : "Noche"}</td>
                                                                        <td className="text-end">${formatMoney(t.antes.valor)} → <b>${formatMoney(t.despues.valor)}</b></td>
                                                                        <td className="text-end">${formatMoney(t.antes.colacion)} → <b>${formatMoney(t.despues.colacion)}</b></td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                ))}

                                <div className="d-flex justify-content-end mt-3">
                                    <button className="btn btn-success btn-sm mb-0" onClick={aplicar} disabled={procesando}>
                                        {procesando ? "Aplicando..." : "Aplicar reajuste"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="card mt-3">
                        <div className="card-body">
                            <h5 className="mb-2">Historial</h5>
                            <div className="row g-3">
                                <div className="col-lg-6">
                                    <div className="fw-semibold small mb-1">Familia</div>
                                    <div className="table-responsive" style={{ maxHeight: 260 }}>
                                        <table className="table table-sm table-bordered small mb-0">
                                            <thead className="table-light sticky-top">
                                                <tr><th>Desde</th><th>Concepto</th><th className="text-end">Anterior</th><th className="text-end">Nuevo</th><th>Usuario</th></tr>
                                            </thead>
                                            <tbody>
                                                {datos.historial_familia.length === 0 && <tr><td colSpan="5" className="text-muted">Sin registros</td></tr>}
                                                {datos.historial_familia.map(h => (
                                                    <tr key={h.id} title={h.observacion || ""}>
                                                        <td>{formatDate(h.fecha_reajuste)}</td>
                                                        <td>{CAMPOS_FAMILIA.find(c => c.key === h.campo)?.label || h.campo}</td>
                                                        <td className="text-end">${formatMoney(h.valor_anterior)}</td>
                                                        <td className="text-end">${formatMoney(h.valor_nuevo)}</td>
                                                        <td>{h.usuario_nombre}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="col-lg-6">
                                    <div className="fw-semibold small mb-1">Acuerdos</div>
                                    <div className="table-responsive" style={{ maxHeight: 260 }}>
                                        <table className="table table-sm table-bordered small mb-0">
                                            <thead className="table-light sticky-top">
                                                <tr><th>Desde</th><th>Auxiliar</th><th className="text-end">Turno</th><th className="text-end">Turnos</th><th>Registrado</th></tr>
                                            </thead>
                                            <tbody>
                                                {datos.historial_acuerdos.length === 0 && <tr><td colSpan="5" className="text-muted">Sin registros</td></tr>}
                                                {datos.historial_acuerdos.map(h => (
                                                    <tr key={h.id} title={h.observacion || ""}>
                                                        <td>{formatDate(h.fecha_reajuste)}</td>
                                                        <td>{h.auxiliarNombre}</td>
                                                        <td className="text-end">${formatMoney(h.valorTurno_anterior)} → ${formatMoney(h.valorTurno_nuevo)}</td>
                                                        <td className="text-end">{h.turnos_actualizados}</td>
                                                        <td>{formatDateTime(h.created_at)} · {h.usuario_nombre}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ReajusteValoresPaciente;
